/**
 * SchemaDriftPanel — review a schema-drift proposal and approve or reject it.
 *
 * Shown in the Run tab after "Check for drift" (POST .../schema-drift) found
 * drift. Each change shows what changed, why it matters, the exact ALTER TABLE
 * statement and the mapping change. Same decision pattern as ProposalTable:
 *   "Approve & apply" → POST /proposals/{id}/apply-drift — runs the DDL on the
 *     target in one transaction, updates the job's mapping, reruns the job
 *   "Reject"          → POST /proposals/{id}/reject — nothing changes
 * Both decisions are written to the audit trail with the person who made them.
 *
 * Safety: renders schema metadata only (column names, types, DDL) — never a
 * data value.
 */

import { useCallback, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import type {
  SchemaDriftApplyResult,
  SchemaDriftChange,
  SchemaDriftCheck,
  SchemaDriftKind,
} from "../../../features/pipeline-engineer/types";
import {
  applySchemaDrift,
  rejectProposal,
} from "../../../features/pipeline-engineer/api/pe-client";

const KIND_CHIP: Record<SchemaDriftKind, { label: string; cls: string }> = {
  add_column: {
    label: "New column",
    cls: "bg-sky-500/10 text-sky-400 border border-sky-500/20",
  },
  map_column: {
    label: "Unmapped column",
    cls: "bg-violet-500/10 text-violet-400 border border-violet-500/20",
  },
  map_type: {
    label: "Mapping type",
    cls: "bg-violet-500/10 text-violet-400 border border-violet-500/20",
  },
  widen_column: {
    label: "Wider column",
    cls: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  },
  removed_column: {
    label: "Removed column",
    cls: "bg-red-500/10 text-red-400 border border-red-500/20",
  },
  type_change: {
    label: "Type changed",
    cls: "bg-red-500/10 text-red-400 border border-red-500/20",
  },
};

interface SchemaDriftPanelProps {
  check: SchemaDriftCheck;
  /** Approved and applied; `result.run` is the queued rerun, if any. */
  onApplied: (result: SchemaDriftApplyResult) => void;
  onRejected: () => void;
  /** Check every job that reads this source (and downstream) instead. */
  onFindImpact?: () => void;
}

export function SchemaDriftPanel({
  check,
  onApplied,
  onRejected,
  onFindImpact,
}: SchemaDriftPanelProps) {
  const [applying, setApplying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A failed apply spends the proposal on the server; only a fresh "Check for drift" can continue. */
  const [spent, setSpent] = useState(false);
  const fixable = check.items.filter((c) => c.autoFix);
  const statements = fixable.filter((c) => c.ddl).length;

  const handleApprove = useCallback(async () => {
    if (!check.proposalId) return;
    const summary =
      `Approve and apply ${fixable.length} change${fixable.length !== 1 ? "s" : ""}? ` +
      `${statements} ALTER TABLE statement${statements !== 1 ? "s" : ""} will run on ${check.targetTable} ` +
      (check.transactional
        ? "in one transaction"
        : "one at a time (this database commits each DDL statement; if one fails, the earlier ones stay applied)") +
      ", the job's mapping will be updated, and the job will rerun.";
    if (!window.confirm(summary)) return;
    setError(null);
    setApplying(true);
    try {
      onApplied(await applySchemaDrift(check.proposalId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply.");
      setSpent(true);
    } finally {
      setApplying(false);
    }
  }, [check, fixable.length, statements, onApplied]);

  const handleReject = useCallback(async () => {
    if (!check.proposalId) return;
    setError(null);
    setRejecting(true);
    try {
      await rejectProposal(check.proposalId);
      onRejected();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject.");
    } finally {
      setRejecting(false);
    }
  }, [check.proposalId, onRejected]);

  return (
    <div
      className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 space-y-3"
      aria-label="Schema drift proposal"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-amber-300">
            Schema drift: {check.items.length} change
            {check.items.length !== 1 ? "s" : ""} found
          </p>
          <p className="text-xs text-white/40 mt-0.5 font-mono">
            {check.sourceTable} ({check.sourceColumnCount} columns) →{" "}
            {check.targetTable} ({check.targetColumnCount} columns,{" "}
            {check.mappedColumnCount} mapped)
          </p>
          {onFindImpact && (
            <button
              type="button"
              onClick={onFindImpact}
              className="mt-1 text-xs text-amber-300 underline hover:text-amber-200"
            >
              Other jobs read {check.sourceTable} too? Find every affected job
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void handleReject()}
            disabled={applying || rejecting || spent}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rejecting ? (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            ) : (
              <X size={12} aria-hidden="true" />
            )}
            Reject
          </button>
          <button
            type="button"
            onClick={() => void handleApprove()}
            disabled={applying || rejecting || spent || fixable.length === 0}
            title={
              fixable.length === 0
                ? "Nothing here can be applied automatically"
                : undefined
            }
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            ) : (
              <Check size={12} aria-hidden="true" />
            )}
            Approve &amp; apply
          </button>
        </div>
      </div>

      {error !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {error}
        </p>
      )}

      <ul className="space-y-2">
        {check.items.map((change) => (
          <DriftChangeRow key={change.changeId} change={change} />
        ))}
      </ul>

      <p className="text-xs text-white/30">
        <strong className="text-white/50">Approve &amp; apply</strong> runs the
        statements above on the target{" "}
        {check.transactional
          ? "in one transaction (all or nothing)"
          : "one at a time (this database commits each one)"}
        , adds the mapping changes and reruns the job.{" "}
        <strong className="text-white/50">Reject</strong> changes nothing. Both
        are recorded in the audit trail with your name. Changes marked{" "}
        <em>manual</em> are never applied automatically.
      </p>
    </div>
  );
}

export function DriftChangeRow({ change }: { change: SchemaDriftChange }) {
  const chip = KIND_CHIP[change.kind] ?? {
    label: change.kind,
    cls: "bg-white/5 text-white/50 border border-white/10",
  };
  const typeLine =
    change.kind === "widen_column"
      ? `${change.targetType} → ${change.proposedType}`
      : (change.proposedType ?? change.sourceType ?? "");
  return (
    <li className="bg-black/20 border border-white/5 rounded-lg p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${chip.cls}`}
        >
          {chip.label}
        </span>
        <span className="text-xs font-mono text-white/80">{change.column}</span>
        {typeLine && (
          <span className="text-xs font-mono text-white/40">{typeLine}</span>
        )}
        {!change.autoFix && (
          <span className="text-xs text-white/40 italic">manual</span>
        )}
      </div>
      <p className="text-xs text-white/60">{change.reason}</p>
      {change.ddl && (
        <pre className="text-xs font-mono text-emerald-300/90 bg-black/30 rounded px-2 py-1 whitespace-pre-wrap break-all">
          {change.ddl};
        </pre>
      )}
      {change.mappingAddition && (
        <p className="text-xs font-mono text-white/40">
          mapping: + {change.mappingAddition.srcExpression} →{" "}
          {change.mappingAddition.tgtCol} ({change.mappingAddition.dataType})
        </p>
      )}
      {change.mappingUpdate && (
        <p className="text-xs font-mono text-white/40">
          mapping: {change.mappingUpdate.tgtCol} type →{" "}
          {change.mappingUpdate.dataType}
        </p>
      )}
    </li>
  );
}
